import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowLeft, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { useCart } from '../context/CartContext';
import { trackEvent, toGaItem } from '../lib/analyticsEvents';

export default function CartPage() {
  const { items, subtotal, total, updateQuantity, removeItem, clearCart } = useCart();

  useEffect(() => {
    if (!items.length) return;

    trackEvent('view_cart', {
      currency: 'CHF',
      value: Number(total || 0),
      items: items.map((item) => toGaItem(item, item.quantity || 1)),
    });
  }, [items, total]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(price);
  };

  const handleRemoveItem = (item) => {
    trackEvent('remove_from_cart', {
      currency: 'CHF',
      value: Number(item.price || 0) * Number(item.quantity || 1),
      items: [toGaItem(item, item.quantity || 1)],
    });

    removeItem(item.id);
  };

  const handleDecreaseQuantity = (item) => {
    if (item.quantity <= 1) {
      trackEvent('remove_from_cart', {
        currency: 'CHF',
        value: Number(item.price || 0),
        items: [toGaItem(item, 1)],
      });

      removeItem(item.id);
      return;
    }

    trackEvent('remove_from_cart', {
      currency: 'CHF',
      value: Number(item.price || 0),
      items: [toGaItem(item, 1)],
    });

    updateQuantity(item.id, item.quantity - 1);
  };

  const handleIncreaseQuantity = (item) => {
    trackEvent('add_to_cart', {
      currency: 'CHF',
      value: Number(item.price || 0),
      items: [toGaItem(item, 1)],
    });

    updateQuantity(item.id, item.quantity + 1);
  };

  const handleClearCart = () => {
    if (items.length) {
      trackEvent('clear_cart', {
        currency: 'CHF',
        value: Number(total || 0),
        items: items.map((item) => toGaItem(item, item.quantity || 1)),
      });
    }

    clearCart();
  };

  const handleBeginCheckout = () => {
    trackEvent('begin_checkout', {
      currency: 'CHF',
      value: Number(total || 0),
      items: items.map((item) => toGaItem(item, item.quantity || 1)),
    });
  };

  return (
    <main data-testid="cart-page" className="min-h-screen pt-20 bg-warm-alabaster">
      {/* Header */}
      <section className="bg-warm-secondary py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h1 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold">
            Votre Panier
          </h1>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-warm-secondary flex items-center justify-center">
                <ShoppingBag className="w-10 h-10 text-soft-charcoal-secondary" />
              </div>
              <h2 className="font-serif text-2xl text-espresso font-semibold mb-3">
                Votre panier est vide
              </h2>
              <p className="text-soft-charcoal-secondary mb-8 max-w-md mx-auto">
                Découvrez nos délicieux cafés et pâtisseries artisanales.
              </p>
              <Button asChild className="btn-primary" data-testid="browse-menu-btn">
                <Link to="/menu">
                  Voir le Menu
                </Link>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-soft-charcoal-secondary">
                    {items.length} article{items.length > 1 ? 's' : ''}
                  </p>
                  <button
                    onClick={handleClearCart}
                    data-testid="clear-cart-btn"
                    className="text-sm text-red-500 hover:text-red-600 transition-colors duration-300"
                  >
                    Vider le panier
                  </button>
                </div>

                <ul className="space-y-6">
                  {items.map((item) => (
                    <li
                      key={item.id}
                      data-testid={`cart-page-item-${item.id}`}
                      className="bg-white p-6 border border-border-subtle"
                    >
                      <div className="flex gap-6">
                        {/* Image */}
                        <Link to={`/produit/${item.slug}`} className="flex-shrink-0">
                          <div className="w-24 h-24 lg:w-32 lg:h-32 bg-warm-secondary overflow-hidden">
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </Link>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <Link to={`/produit/${item.slug}`}>
                            <h3 className="font-serif text-lg font-semibold text-espresso hover:text-latte-gold transition-colors duration-300">
                              {item.name}
                            </h3>
                          </Link>
                          <p className="text-soft-charcoal-secondary text-sm mt-1">
                            {item.short_description}
                          </p>
                          <p className="text-latte-gold font-semibold mt-2">
                            {formatPrice(item.price)}
                          </p>

                          {/* Actions */}
                          <div className="flex items-center justify-between mt-4">
                            {/* Quantity */}
                            <div className="flex items-center border border-border-subtle">
                              <button
                                onClick={() => handleDecreaseQuantity(item)}
                                data-testid={`cart-decrease-${item.id}`}
                                className="w-10 h-10 flex items-center justify-center hover:bg-warm-secondary transition-colors duration-300"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-12 text-center font-medium">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleIncreaseQuantity(item)}
                                data-testid={`cart-increase-${item.id}`}
                                className="w-10 h-10 flex items-center justify-center hover:bg-warm-secondary transition-colors duration-300"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Subtotal & Remove */}
                            <div className="flex items-center gap-4">
                              <span className="font-serif font-semibold text-espresso">
                                {formatPrice(item.price * item.quantity)}
                              </span>
                              <button
                                onClick={() => handleRemoveItem(item)}
                                data-testid={`cart-remove-${item.id}`}
                                className="text-soft-charcoal-secondary hover:text-red-500 transition-colors duration-300"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/menu"
                  className="inline-flex items-center mt-8 text-soft-charcoal-secondary hover:text-espresso transition-colors duration-300"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Continuer mes achats
                </Link>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white p-6 border border-border-subtle sticky top-28">
                  <h2 className="font-serif text-xl font-semibold text-espresso mb-6">
                    Récapitulatif
                  </h2>

                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-soft-charcoal-secondary">Sous-total</span>
                      <span className="text-soft-charcoal">{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-soft-charcoal-secondary">Livraison</span>
                      <span className="text-soft-charcoal">Retrait sur place</span>
                    </div>
                    <Separator className="bg-border-subtle" />
                    <div className="flex justify-between">
                      <span className="font-serif font-semibold text-espresso">Total</span>
                      <span className="font-serif font-semibold text-espresso text-xl">
                        {formatPrice(total)}
                      </span>
                    </div>
                  </div>

                  <Button
                    asChild
                    className="w-full btn-primary mt-6"
                    data-testid="proceed-checkout-btn"
                  >
                    <Link to="/checkout" onClick={handleBeginCheckout}>
                      Passer au Paiement
                    </Link>
                  </Button>

                  <p className="text-xs text-soft-charcoal-secondary text-center mt-4">
                    Paiement sécurisé par Stripe
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}